import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { fetchProducts, imgUrl } from "@/lib/api";
import { lifestyleFor } from "@/data/lifestyle";
import { Motif } from "@/lib/motifs";
import { Butterfly } from "@/components/site/LogoMark";
import Turntable from "@/components/product/Turntable";
import { LIFESTYLE_MAP } from "@/data/lifestyle";

const price = (n) => `₹${n.toLocaleString("en-IN")}`;

const buildFrames = (product) => {
  const byRole = {};
  (product.images || []).forEach((im) => { byRole[im.role] = im.url; });
  const life = LIFESTYLE_MAP[product.slug];
  const stack = [
    life,
    byRole.hero,
    byRole.back,
    byRole.main,
    ...(product.turntable_frames || []),
  ].filter(Boolean);
  return Array.from(new Set(stack));
};

const bubblesFor = (product) => {
  const byRole = {};
  (product.images || []).forEach((im) => { byRole[im.role] = im.url; });
  const seed = product.hotspots && product.hotspots.length ? product.hotspots : [];
  if (seed.length) return seed.slice(0, 3);
  return ["embroidery", "fabric", "button"]
    .filter((k) => byRole[k])
    .slice(0, 3)
    .map((k) => ({ key: k, image: byRole[k] }));
};

const bubbleLabel = (k) => ({
  embroidery: "Hand-embroidered stitches",
  fabric: "Organic cotton weave",
  button: "Coconut wood buttons",
  stitch: "Hand-finished",
  lining: "Silk-touch lining",
}[k] || k);

const Frame = ({ id, label, aside, children, dark }) => (
  <section id={id} className="hairline-b py-14 md:py-20 scroll-mt-32" data-testid={`shop-preview-${id}`}>
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mb-8 flex flex-wrap items-baseline gap-6">
      <div className="w-8 h-8 rounded-full hairline flex items-center justify-center text-xs">
        {id.split("-").pop().toUpperCase()}
      </div>
      <h2 className="serif text-3xl md:text-4xl">{label}</h2>
      <p className="text-sm text-ink/60 max-w-[56ch]">{aside}</p>
    </div>
    <div className={dark ? "py-8" : ""} style={dark ? { background: "var(--charcoal)", color: "var(--ground)" } : {}}>
      {children}
    </div>
  </section>
);

/* ============================================================ */
/* Option A · Tabloid Masthead — huge name spans, image below */
/* ============================================================ */
const OptionA = ({ items }) => (
  <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 space-y-24">
    {items.slice(0, 3).map((p, i) => {
      const flip = i % 2 === 1;
      return (
        <article key={p.slug} className="hairline-b pb-16" data-testid={`optA-shop-${p.slug}`}>
          <header className="flex items-baseline justify-between mb-10">
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink/60 flex items-center gap-3">
              <Motif name={p.motif} size={14} strokeColor="var(--accent)" />
              {String(i + 1).padStart(2, "0")} · {p.motif}
            </div>
            <div className="text-[10px] tracking-[0.32em] uppercase text-ink/40">{p.age_range}</div>
          </header>
          <h3 className="serif text-[80px] md:text-[128px] leading-[0.9] tracking-tight text-ink">{p.name}</h3>
          <div className={`mt-10 grid md:grid-cols-12 gap-10 items-start ${flip ? "md:[direction:rtl]" : ""}`}>
            <div className="md:col-span-8 md:[direction:ltr]">
              <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name}
                className="w-full aspect-[16/9] object-cover" style={{ objectPosition: "center 25%" }} />
            </div>
            <div className="md:col-span-4 md:[direction:ltr]">
              <p className="text-ink/80 leading-relaxed">{p.hook}</p>
              <div className="mt-6 serif text-3xl">{price(p.price)}</div>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to={`/product/${p.slug}`} className="btn-solid" data-testid={`optA-buy-${p.slug}`}>Add to bag</Link>
                <Link to={`/product/${p.slug}`} className="crimson-link nav-item !text-[11px] self-center">See the piece →</Link>
              </div>
            </div>
          </div>
        </article>
      );
    })}
  </div>
);

/* ============================================================ */
/* Option B · 2-up Boutique — two products side by side          */
/* ============================================================ */
const OptionB = ({ items }) => {
  const pairs = [];
  for (let i = 0; i < items.length; i += 2) pairs.push([items[i], items[i + 1]].filter(Boolean));
  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 space-y-16">
      {pairs.slice(0, 3).map((pair, ri) => (
        <div key={ri} className="grid md:grid-cols-2 gap-8 md:gap-16">
          {pair.map((p) => (
            <div key={p.slug} className="relative hairline p-6 md:p-8" data-testid={`optB-shop-${p.slug}`}>
              <div className="overflow-hidden bg-sand aspect-[4/5] mb-6">
                <img src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))} alt={p.name} className="w-full h-full object-cover" />
              </div>
              <div className="flex items-baseline justify-between mb-2">
                <div className="serif text-2xl md:text-3xl">{p.name}</div>
                <div className="serif text-lg">{price(p.price)}</div>
              </div>
              <div className="flex items-center gap-3 text-xs text-ink/60 mb-4">
                <Motif name={p.motif} size={14} strokeColor="var(--accent)" />
                <span>{p.motif}</span><span>·</span><span>{p.age_range}</span>
              </div>
              <p className="text-sm text-ink/75 leading-relaxed mb-6">{p.hook}</p>
              <div className="flex gap-3">
                <Link to={`/product/${p.slug}`} className="btn-ink !py-3 !px-6 text-[10px]" data-testid={`optB-buy-${p.slug}`}>Add to bag</Link>
                <Link to={`/product/${p.slug}`} className="crimson-link nav-item !text-[11px] self-center">Details →</Link>
              </div>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};

/* ============================================================ */
/* Option C · Cinematic Full-bleed — 100vh per product           */
/* ============================================================ */
const OptionC = ({ items }) => (
  <div>
    {items.slice(0, 3).map((p, i) => {
      const dark = i % 2 === 0;
      return (
        <section
          key={p.slug}
          className="relative w-full flex items-end"
          style={{ height: "100vh", minHeight: 560, background: dark ? "var(--charcoal)" : "var(--ground)" }}
          data-testid={`optC-shop-${p.slug}`}
        >
          <img
            src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
            alt={p.name}
            className="absolute inset-0 w-full h-full object-cover"
            style={{ opacity: dark ? 0.9 : 1, objectPosition: "center 30%" }}
          />
          <div className="absolute inset-0" style={{
            background: dark
              ? "linear-gradient(180deg, rgba(28,26,25,0.05) 0%, rgba(28,26,25,0.65) 80%)"
              : "linear-gradient(180deg, rgba(246,241,233,0.05) 0%, rgba(246,241,233,0.75) 80%)"
          }} />
          <div className="relative z-10 max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 pb-16 md:pb-24 w-full">
            <div className={`max-w-[540px] ${dark ? "text-ground" : "text-ink"}`}>
              <div className="text-[10px] tracking-[0.32em] uppercase opacity-70 mb-4 flex items-center gap-3">
                <Motif name={p.motif} size={14} strokeColor={dark ? "#F6F1E9" : "var(--accent)"} />
                {p.motif} · {p.age_range}
              </div>
              <h3 className="serif text-6xl md:text-7xl leading-[1.02]">{p.name}</h3>
              <p className="mt-5 max-w-[42ch] leading-relaxed text-sm opacity-90">{p.hook}</p>
              <div className="mt-6 flex items-center gap-6">
                <span className="serif text-3xl">{price(p.price)}</span>
                <Link
                  to={`/product/${p.slug}`}
                  className={`inline-block px-6 py-3 rounded-full text-[10px] tracking-[0.22em] uppercase border ${dark ? "border-ground text-ground hover:bg-ground hover:text-ink" : "border-ink text-ink hover:bg-ink hover:text-ground"} transition-colors`}
                  data-testid={`optC-buy-${p.slug}`}
                >
                  Add to bag
                </Link>
              </div>
            </div>
          </div>
        </section>
      );
    })}
  </div>
);

/* ============================================================ */
/* Option D · Sticky Sidebar — panel persists, products scroll  */
/* ============================================================ */
const OptionD = ({ items }) => {
  const [active, setActive] = useState(0);
  const refs = useRef([]);
  useEffect(() => {
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting && e.intersectionRatio > 0.5) {
          const i = Number(e.target.getAttribute("data-idx"));
          if (!Number.isNaN(i)) setActive(i);
        }
      });
    }, { threshold: [0.55] });
    refs.current.forEach((el) => el && io.observe(el));
    return () => io.disconnect();
  }, [items.length]);

  const cur = items[active] || items[0];
  if (!cur) return null;
  return (
    <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24">
      <div className="grid md:grid-cols-12 gap-10">
        <aside className="md:col-span-4 md:sticky md:top-32 h-fit" data-testid="optD-sidebar">
          <div className="text-[10px] tracking-[0.32em] uppercase text-ink/50 mb-3 flex items-center gap-3">
            <Motif name={cur.motif} size={14} strokeColor="var(--accent)" />
            Now viewing · {String(active + 1).padStart(2, "0")}/{items.length}
          </div>
          <h3 className="serif text-5xl md:text-6xl leading-[1.02] transition-opacity duration-500" key={cur.slug}>{cur.name}</h3>
          <p className="mt-4 text-ink/75 leading-relaxed text-sm max-w-[36ch]">{cur.hook}</p>
          <div className="mt-6 serif text-3xl">{price(cur.price)}</div>
          <div className="text-xs text-ink/60 mt-1">{cur.age_range}</div>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to={`/product/${cur.slug}`} className="btn-solid" data-testid="optD-buy">Add to bag</Link>
            <Link to={`/product/${cur.slug}`} className="crimson-link nav-item !text-[11px] self-center">See the piece →</Link>
          </div>
          <div className="mt-10 flex flex-col gap-2 text-xs" data-testid="optD-index">
            {items.map((p, i) => (
              <button
                key={p.slug}
                onClick={() => refs.current[i]?.scrollIntoView({ behavior: "smooth", block: "center" })}
                className={`text-left transition-colors ${active === i ? "text-accent" : "text-ink/50 hover:text-ink"}`}
                data-testid={`optD-jump-${p.slug}`}
              >
                <span className="mr-3">{String(i + 1).padStart(2, "0")}</span>{p.name}
              </button>
            ))}
          </div>
        </aside>
        <div className="md:col-span-8 space-y-6">
          {items.slice(0, 5).map((p, i) => (
            <div
              key={p.slug}
              ref={(el) => (refs.current[i] = el)}
              data-idx={i}
              className="relative overflow-hidden bg-sand"
              data-testid={`optD-slide-${p.slug}`}
            >
              <img
                src={imgUrl(lifestyleFor(p.slug, (p.images || [{}])[0].url))}
                alt={p.name}
                className="w-full aspect-[4/5] md:aspect-[5/6] object-cover"
              />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ============================================================ */
/* Page                                                          */
/* ============================================================ */
const ShopPreview = () => {
  const [items, setItems] = useState([]);
  useEffect(() => {
    fetchProducts({ category: "girls" }).then((all) => setItems(all.slice(0, 6)));
  }, []);
  const ready = items.length >= 4;

  return (
    <div className="pt-32 md:pt-40 pb-24" data-testid="shop-preview-page">
      <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 mb-14">
        <div className="flex items-center gap-3 mb-4">
          <Butterfly size={22} />
          <span className="nav-item text-ink/60">Preview · Shop layouts</span>
        </div>
        <h1 className="serif text-5xl md:text-6xl leading-[1.02]">
          Four ways to walk the <em className="italic">shop</em>.
        </h1>
        <p className="mt-6 text-ink/70 max-w-[60ch] leading-relaxed">
          Same six pieces, four different shop layouts. Tell me the letter that lands
          and I&rsquo;ll wire it as the new /shop.
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <a href="#shop-opt-a" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">A · Tabloid Masthead</a>
          <a href="#shop-opt-b" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">B · 2-up Boutique</a>
          <a href="#shop-opt-c" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">C · Cinematic Full-bleed</a>
          <a href="#shop-opt-d" className="nav-item hairline px-3 py-2 hover:bg-ink hover:text-ground">D · Sticky Sidebar</a>
        </div>
      </div>

      {!ready ? (
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 lg:px-24 text-sm text-ink/60">Loading pieces…</div>
      ) : (
        <>
          <Frame id="shop-opt-a" label="Option A · Tabloid Masthead" aside="Each product owns a full page-height block with an enormous serif name banner across the top, a wide 16:9 image below and a small buy sidebar. Newspaper front-page energy.">
            <OptionA items={items} />
          </Frame>
          <Frame id="shop-opt-b" label="Option B · 2-up Boutique" aside="Two products side-by-side per row inside a hairline frame — like the two mannequins in a shop window. Denser but still quiet.">
            <OptionB items={items} />
          </Frame>
          <Frame id="shop-opt-c" label="Option C · Cinematic Full-bleed" aside="One product per full viewport, alternating dark and light. No card, no chrome — just the image, the piece name in six-column serif, price and Add to bag. Fashion editorial cover flow.">
            <OptionC items={items} />
          </Frame>
          <Frame id="shop-opt-d" label="Option D · Sticky Sidebar" aside="A single sticky product panel on the left updates itself as you scroll the images on the right. Numbered index doubles as a fast jump list. Museum-tour feel.">
            <OptionD items={items} />
          </Frame>
        </>
      )}
    </div>
  );
};

export default ShopPreview;
